import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, 
  ChevronRight, 
  Folder, 
  FolderOpen, 
  Package, 
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { CategoriaData, ProductoConImagen } from '../hooks/useFetchStockData';
import ProductoItem from './ProductoItem';

interface CategoriaFolderProps {
  categoria: CategoriaData;
  onProductSelect?: (producto: ProductoConImagen) => void;
  selectedProduct?: ProductoConImagen | null;
  showImages?: boolean;
  showAllFields?: boolean;
  onRefresh?: (categoryName: string) => void;
  loading?: boolean;
}

const CategoriaFolder: React.FC<CategoriaFolderProps> = ({
  categoria,
  onProductSelect,
  selectedProduct,
  showImages = true,
  showAllFields = true,
  onRefresh,
  loading = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleRefresh = async () => {
    if (onRefresh && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh(categoria.name);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const isProductSelected = (producto: ProductoConImagen): boolean => {
    return selectedProduct?.sku === producto.sku;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800"
    >
      {/* Header de la categoría */}
      <div className="p-4 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <button
            onClick={handleToggle}
            className="flex items-center space-x-3 flex-1 text-left hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg p-2 -m-2 transition-colors duration-200"
          >
            <div className="flex items-center space-x-3">
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              )}
              
              <div className="flex items-center space-x-2">
                {isExpanded ? (
                  <FolderOpen className="w-6 h-6 text-primary-600" />
                ) : (
                  <Folder className="w-6 h-6 text-gray-500" />
                )}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    {categoria.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {categoria.isEmpty 
                      ? 'Sin productos' 
                      : `${categoria.productos.length} producto${categoria.productos.length !== 1 ? 's' : ''}`
                    }
                  </p>
                </div>
              </div>
            </div>
          </button>

          {/* Botón de actualizar */}
          {onRefresh && (
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Actualizar productos"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* Contenido expandible */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4">
              {loading || isRefreshing ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-300">
                    Cargando productos...
                  </span>
                </div>
              ) : categoria.isEmpty ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No hay productos para mostrar en esta categoría.
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Los productos aparecerán aquí cuando se agreguen a la categoría <strong>{categoria.name}</strong>
                  </p>
                  <button
                    onClick={handleRefresh}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm"
                  >
                    Actualizar
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Productos disponibles:
                  </div>
                  {categoria.productos.map((producto) => (
                    <ProductoItem
                      key={producto.sku}
                      producto={producto}
                      onSelect={onProductSelect}
                      selected={isProductSelected(producto)}
                      showImage={showImages}
                      showAllFields={showAllFields}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CategoriaFolder;
